import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import PrivateRoute from "./auth/PrivateRoute";
import AdminRoute from "./auth/AdminRoute";
import MyProfile from "./pages/MyProfile";
import ErrorPage from "./pages/ErrorPage";
import RouteScrollToTop from "./helper/RouteScrollToTop";
import Dashboard from "./pages/Dashboard";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import PaymentList from "./pages/PaymentList";
import InstanceList from "./pages/InstanceList";
import ScanInstance from "./pages/ScanInstance";
import SinglePage from "./pages/message/SinglePage";
import MediaPage from "./pages/message/MediaPage";
import ContactPage from "./pages/message/ContactPage";
import LocationPage from "./pages/message/LocationPage";
import GroupPage from "./pages/message/GroupPage";
import TemplateList from "./pages/TemplateList";
import MessageLogList from "./pages/MessageLogList";
import PhonebookList from "./pages/PhonebookList";
import CampaignList from "./pages/CampaignList";
import CampaignLogs from "./pages/CampaignLogs";
import APIAccess from "./pages/APIAccess";
import WebhookSetting from "./pages/WebhookSetting";
import Setting from "./pages/Setting";
import Chat from "./pages/Chat";
import Automation from "./pages/Automation";
import QuickReplies from "./pages/QuickReplies";
import MediaLibrary from "./pages/MediaLibrary";
import SupportPing from "./pages/SupportPing";

import AdminSignInPage from "./pages/admin/AdminSignInPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminPings from "./pages/admin/AdminPings";
import AdminPlans from "./pages/admin/AdminPlans";
import AdminPages from "./pages/admin/AdminPages";
import AdminFaqs from "./pages/admin/AdminFaqs";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminProfile from "./pages/admin/AdminProfile";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminTestEmail from "./pages/admin/AdminTestEmail";
import PublicFaqs from "./pages/public/PublicFaqs";
import PublicTestimonials from "./pages/public/PublicTestimonials";
import PublicPricing from "./pages/public/PublicPricing";
import PublicPage from "./pages/public/PublicPage";
import LandingPage from "./pages/LandingPage";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <RouteScrollToTop />
        <Routes>
          <Route exact path="/" element={<LandingPage />} />
          <Route exact path="/login" element={<SignInPage />} />
          <Route exact path="/register" element={<SignUpPage />} />

          {/* Public CMS (no login) — content from Admin → Pages / FAQs / Testimonials */}
          <Route exact path="/faqs" element={<PublicFaqs />} />
          <Route exact path="/testimonials" element={<PublicTestimonials />} />
          <Route exact path="/pricing" element={<PublicPricing />} />
          <Route exact path="/p/:slug" element={<PublicPage />} />

          <Route exact path="/dashboard" element={<PrivateRoute element={Dashboard} />} />

          {/* User area */}
          <Route exact path="/plans" element={<PrivateRoute element={PaymentList} />} />
          <Route exact path="/payments" element={<Navigate to="/plans" replace />} />
          <Route exact path="/instances" element={<PrivateRoute element={InstanceList} />} />
          <Route exact path="/scan-instance" element={<PrivateRoute element={ScanInstance} />} />
          <Route exact path="/chat-single" element={<PrivateRoute element={SinglePage} />} />
          <Route exact path="/chat-image" element={<PrivateRoute element={MediaPage} />} />
          <Route exact path="/chat-audio" element={<PrivateRoute element={MediaPage} />} />
          <Route exact path="/chat-video" element={<PrivateRoute element={MediaPage} />} />
          <Route exact path="/chat-contact" element={<PrivateRoute element={ContactPage} />} />
          <Route exact path="/chat-location" element={<PrivateRoute element={LocationPage} />} />
          <Route exact path="/chat-poll" element={<PrivateRoute element={MyProfile} />} />
          <Route exact path="/chat-reply" element={<PrivateRoute element={MyProfile} />} />
          <Route exact path="/chat-group" element={<PrivateRoute element={GroupPage} />} />
          <Route exact path="/templates" element={<PrivateRoute element={TemplateList} />} />
          <Route exact path="/logs" element={<PrivateRoute element={MessageLogList} />} />
          <Route exact path="/phonebooks" element={<PrivateRoute element={PhonebookList} />} />
          <Route exact path="/campaigns" element={<PrivateRoute element={CampaignList} />} />
          <Route exact path="/campaigns/:campaignId/logs" element={<PrivateRoute element={CampaignLogs} />} />
          <Route exact path="/api-access" element={<PrivateRoute element={APIAccess} />} />
          <Route exact path="/webhook-settings" element={<PrivateRoute element={WebhookSetting} />} />
          <Route exact path="/chat-messages" element={<PrivateRoute element={Chat} />} />
          <Route exact path="/automation" element={<PrivateRoute element={Automation} />} />
          <Route exact path="/quick-replies" element={<PrivateRoute element={QuickReplies} />} />
          <Route exact path="/media-library" element={<PrivateRoute element={MediaLibrary} />} />
          <Route exact path="/my-profile" element={<PrivateRoute element={MyProfile} />} />
          <Route exact path="/settings" element={<PrivateRoute element={Setting} />} />
          <Route exact path="/support" element={<PrivateRoute element={SupportPing} />} />

          {/* Admin area — same project, role-gated */}
          <Route exact path="/admin/login" element={<AdminSignInPage />} />
          <Route exact path="/admin" element={<AdminRoute element={AdminDashboard} />} />
          <Route exact path="/admin/dashboard" element={<AdminRoute element={AdminDashboard} />} />
          <Route exact path="/admin/users" element={<AdminRoute element={AdminUsers} />} />
          <Route exact path="/admin/orders" element={<AdminRoute element={AdminOrders} />} />
          <Route exact path="/admin/pings" element={<AdminRoute element={AdminPings} />} />
          <Route exact path="/admin/plans" element={<AdminRoute element={AdminPlans} />} />
          <Route exact path="/admin/pages" element={<AdminRoute element={AdminPages} />} />
          <Route exact path="/admin/faqs" element={<AdminRoute element={AdminFaqs} />} />
          <Route exact path="/admin/testimonials" element={<AdminRoute element={AdminTestimonials} />} />
          <Route exact path="/admin/profile" element={<AdminRoute element={AdminProfile} />} />
          <Route exact path="/admin/payments" element={<AdminRoute element={AdminPayments} />} />
          <Route exact path="/admin/settings" element={<AdminRoute element={AdminSettings} />} />
          <Route exact path="/admin/test-email" element={<AdminRoute element={AdminTestEmail} />} />

          <Route exact path="*" element={<ErrorPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
